import cn from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import filterTextLibraryEntries from 'editor/src/store/editorModules/textLibrary/util/filterTextLibraryEntries';
import { useSelector } from 'editor/src/store/hooks';

import ContentBlock from 'editor/src/component/DesktopSidebar/TabContents/Elements/ContentBlock';
import Search from 'editor/src/component/DesktopSidebar/TabContents/Elements/Search';
import { TabContentProps } from 'editor/src/component/DesktopSidebar/TabContents/index';
import TabContentHeader from 'editor/src/component/DesktopSidebar/TabContents/TabContentHeader';
import SelectButtonGroup from 'editor/src/component/SelectButtonGroup';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';

import { DEFAULT_TEXT_LIBRARY_TITLE_KEY } from './constants';
import EntryDetails from './EntryDetails';
import EntryList from './EntryList';

import styles from './index.module.scss';

function TextLibraryTabContent({ title }: TabContentProps) {
  const { t } = useTranslation();
  const libraryTitle = useSelector((state) => state.editorModules.textLibrary.title);
  const entries = useSelector((state) => state.editorModules.textLibrary.entries);
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string>();
  const [activeCollection, setActiveCollection] = useState<string>();

  // collections in the order the host lists them; a filter only shows when there are several
  const collections = useMemo(
    () => [...new Set(entries.flatMap((entry) => (entry.collection ? [entry.collection] : [])))],
    [entries],
  );
  const collection = activeCollection && collections.includes(activeCollection) ? activeCollection : collections[0];
  const collectionEntries = useMemo(
    () => (collections.length > 1 ? entries.filter((entry) => entry.collection === collection) : entries),
    [entries, collections, collection],
  );
  const results = useMemo(() => filterTextLibraryEntries(collectionEntries, query), [collectionEntries, query]);
  const selectedEntry = selectedId ? entries.find((entry) => entry.id === selectedId) : undefined;

  return (
    <>
      <TabContentHeader title={title || libraryTitle || t(DEFAULT_TEXT_LIBRARY_TITLE_KEY)} />
      {selectedEntry ? (
        <ContentBlock scroll noBorder className={styles.content}>
          <EntryDetails
            // a host may re-send an entry with other verses: start the verse selection over
            key={`${selectedEntry.id}:${selectedEntry.verses.join('\u0000')}`}
            entry={selectedEntry}
            onBack={() => setSelectedId(undefined)}
          />
        </ContentBlock>
      ) : (
        <>
          <ContentBlock noBorder>
            {collections.length > 1 && (
              <SelectButtonGroup
                className={cn(styles.collections, 'cy-text-library-collections')}
                items={collections.map((name) => ({ label: name, value: name }))}
                value={collection}
                onChange={setActiveCollection}
                fullWidth
              />
            )}
            <Search isMobile={isMobile} onChange={setQuery} value={query} />
            {results.length > 0 && (
              <div className={cn(styles.count, 'cy-text-library-count')}>
                {results.length === 1
                  ? t('editor-text-library-count-one')
                  : t('editor-text-library-count', { count: results.length })}
              </div>
            )}
          </ContentBlock>
          <ContentBlock scroll noBorder className={styles.content}>
            <EntryList entries={results} onSelect={setSelectedId} />
          </ContentBlock>
        </>
      )}
    </>
  );
}

export default React.memo(TextLibraryTabContent);
